import { useMemo } from "react";

import { Destination, FormField, parseGcpIamRoleMetadata, parseServiceAccountKey } from "@prequel/react";
import { PasswordDot, TextArea } from "@prequel/shared-components";

import { WrapperProps } from "..";

import { MagicLink } from "@/types";
import { Source } from "@/types/sources";
import { SourceFormField } from "@/types/sources/form";

type TextAreaWrapperProps<
  F extends FormField | SourceFormField,
  C extends Destination | Source | MagicLink,
> = WrapperProps<F & { form_element: "textarea" }, C>;
const TextAreaWrapper = <F extends FormField | SourceFormField, C extends Destination | Source | MagicLink>({
  field,
  connection,
  setConnectionField,
  disabled,
  isEditing,
}: TextAreaWrapperProps<F, C>) => {
  const serviceAccountKeyIsValid = useMemo(() => {
    if ("service_account_key" in connection) {
      return !!parseServiceAccountKey(connection.service_account_key);
    }
    return true;
  }, [connection]);

  const gcpIamRoleMetadataIsValid = useMemo(() => {
    if ("workload_identity_federation_metadata" in connection) {
      return !!parseGcpIamRoleMetadata(connection.workload_identity_federation_metadata);
    }
    return true;
  }, [connection]);

  return (
    <TextArea
      id={field.name}
      label={field.label}
      placeholder={isEditing ? PasswordDot.repeat(184) : field.placeholder}
      overwriteOnly={isEditing}
      subtext={field.description}
      value={connection[field.name]?.toString()}
      onChangeHandler={(value: string) => {
        setConnectionField(field.name as keyof C, value);
      }}
      invalid={
        field.name === "service_account_key"
          ? !serviceAccountKeyIsValid
          : field.name === "workload_identity_federation_metadata"
            ? !gcpIamRoleMetadataIsValid
            : false
      }
      required={field.required && !isEditing}
      disabled={disabled}
    />
  );
};

export default TextAreaWrapper;
